import { GraphQLClient } from 'graphql-request';
import { gql } from 'urql';
import {
  CreateFileAttachmentInput,
  DeleteFileAttachmentInput,
  FileAttachmentList,
  Mutation,
  Query,
  QueryFileAttachmentsForItemArgs,
} from '../generated/graphql';
import { FileAttachmentFragment } from './fragments';

const createFileAttachmentMutation = gql`
  ${FileAttachmentFragment}
  mutation CreateFileAttachment($input: CreateFileAttachmentInput!) {
    createFileAttachment(input: $input) {
      fileAttachment {
        ...FileAttachmentFragment
      }
    }
  }
`;

export const clientCreateFileAttachment = async (client: GraphQLClient, input: CreateFileAttachmentInput) => {
  const res = await client.request<Pick<Mutation, 'createFileAttachment'>>(createFileAttachmentMutation, { input });
  return res.createFileAttachment;
};

const fileAttachmentsForItemQuery = gql`
  ${FileAttachmentFragment}
  query FileAttachmentsForItem($tenantID: ID!, $id: ID!, $cursor: String) {
    fileAttachmentsForItem(tenantID: $tenantID, id: $id, cursor: $cursor) {
      items {
        ...FileAttachmentFragment
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const MAX_FILE_ATTACHMENT_PAGES = 200;

export const clientGetFileAttachmentsForItem = async (
  client: GraphQLClient,
  args: QueryFileAttachmentsForItemArgs
): Promise<FileAttachmentList> => {
  const response = await client.request<Pick<Query, 'fileAttachmentsForItem'>>(fileAttachmentsForItemQuery, args);
  const result = response.fileAttachmentsForItem;
  let pageCount = 1;

  while (result.pageInfo.hasNextPage) {
    if (!result.pageInfo.endCursor) {
      throw new Error('File attachment pagination is missing an end cursor.');
    }
    if (++pageCount > MAX_FILE_ATTACHMENT_PAGES) {
      throw new Error('File attachment pagination exceeded the maximum page count.');
    }
    const nextResponse = await client.request<Pick<Query, 'fileAttachmentsForItem'>>(fileAttachmentsForItemQuery, {
      ...args,
      cursor: result.pageInfo.endCursor,
    });
    result.items.push(...nextResponse.fileAttachmentsForItem.items);
    result.pageInfo = nextResponse.fileAttachmentsForItem.pageInfo;
  }

  return result;
};

const deleteFileAttachmentMutation = gql`
  mutation DeleteFileAttachment($input: DeleteFileAttachmentInput!) {
    deleteFileAttachment(input: $input) {
      changeToken
    }
  }
`;

export const clientDeleteFileAttachment = async (client: GraphQLClient, input: DeleteFileAttachmentInput) => {
  const res = await client.request<Pick<Mutation, 'deleteFileAttachment'>>(deleteFileAttachmentMutation, { input });
  return res.deleteFileAttachment;
};
